/**
 * Seats — who on a family plan can make new things (seat unit, 2026-10-22).
 *
 * Operator ruling: the account holder holds a seat by default, then kids in
 * the order they were added; a grown-up chooses. Members without a seat keep
 * everything they made and can open and play it — they just cannot build.
 *
 * Pure helpers so the plan-change picker, the Apple "seat choice pending"
 * prompt and the plan card all order and default the same way.
 */
import type { ProfileListResponse } from "../api";
import type { KeptPickerMember } from "./KeptKidPicker";

const KID_TINTS: KeptPickerMember["tint"][] = ["sky", "coral", "sand"];

/**
 * The family as the picker shows it: the account holder first (shown as
 * "You" when that is who is looking), then kids by when they were added,
 * then any other grown-up. `created_at` sorts the kids when the roster
 * carries it; otherwise the roster's own order stands.
 */
export function seatMembers(roster: ProfileListResponse, accountHolderId: string | null | undefined): KeptPickerMember[] {
  const holderId = accountHolderId ?? roster.parents.find((p) => p.is_current_user)?.id ?? null;
  const parent = (p: ProfileListResponse["parents"][number]): KeptPickerMember => ({
    id: p.id,
    name: p.is_current_user ? "You" : (p.username ?? "Parent"),
    role: "parent",
    tint: p.is_current_user ? "lavender" : "grass",
    you: p.is_current_user,
  });
  const holder = roster.parents.filter((p) => p.id === holderId).map(parent);
  const others = roster.parents.filter((p) => p.id !== holderId).map(parent);
  const kids = roster.children
    .map((c, i) => ({ c, i }))
    .sort((a, b) => {
      const ta = a.c.created_at ? Date.parse(a.c.created_at) : NaN;
      const tb = b.c.created_at ? Date.parse(b.c.created_at) : NaN;
      if (Number.isNaN(ta) || Number.isNaN(tb) || ta === tb) return a.i - b.i;
      return ta - tb;
    })
    .map(({ c }, i): KeptPickerMember => ({
      id: c.id,
      name: c.username ?? "",
      role: "kid",
      tint: KID_TINTS[i % KID_TINTS.length],
      avatarUrl: c.avatar_url,
    }));
  return [...holder, ...kids, ...others];
}

/**
 * Who holds a seat before anyone chooses: the current holders that are still
 * in the family (in their order), topped up from `members` in order, cut to
 * `count`. With no holders on record that is the account holder, then kids.
 */
export function defaultSeatHolders(members: KeptPickerMember[], count: number, holderIds: string[] = []): string[] {
  const pickable = members.filter((m) => !m.ownPlan).map((m) => m.id);
  const kept = holderIds.filter((id) => pickable.includes(id));
  const extra = pickable.filter((id) => !kept.includes(id));
  return [...kept, ...extra].slice(0, Math.max(0, count));
}

/** sessionStorage: the pending-seat prompt (BillingChrome) has opened in this visit. */
export const SEAT_PROMPT_KEY = "wbm.seat_choice_prompted";

/**
 * The plan card's line naming who builds: "Building: You and Ada · 2 of 3
 * spots". Before the roster loads (or for an id it does not know) it falls
 * back to the count alone.
 */
export function seatHolderLine(holderIds: string[], count: number, members: KeptPickerMember[] | null): string {
  const spots = `${holderIds.length} of ${count} building ${count === 1 ? "spot" : "spots"}`;
  const names = holderIds.map((id) => members?.find((m) => m.id === id)?.name);
  if (holderIds.length === 0 || names.some((n) => !n)) return `Building: ${spots}`;
  const list = names.length <= 2 ? names.join(" and ") : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
  return `Building: ${list} · ${spots}`;
}
